import { type AnalyticsFilter, calculatePortfolioMetrics } from "@seed-factory/core";
import { format, startOfMonth } from "date-fns";
import JSZip from "jszip";
import { buildSeries } from "./analytics";
import { appConfig } from "./config";
import { civilDateValue } from "./dates";
import { buildFunnel } from "./funnel";
import type { EstufaData } from "./local-types";

export type ExportDatasetMap = Record<string, unknown[]>;

function escapeCsv(value: unknown): string {
  const text =
    value === null || value === undefined
      ? ""
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}
function toCsv(rows: unknown[]): string {
  if (rows.length === 0) return "";
  const objects = rows as Record<string, unknown>[];
  const keys = [...new Set(objects.flatMap((row) => Object.keys(row)))];
  return [
    keys.map(escapeCsv).join(","),
    ...objects.map((row) => keys.map((key) => escapeCsv(row[key])).join(",")),
  ].join("\n");
}
function downloadBlob(content: BlobPart, type: string, filename: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
function inWindow(value: string | null, filter: AnalyticsFilter) {
  if (!value) return false;
  const date = new Date(value);
  return date >= filter.from && date <= filter.to;
}
function overlapsWindow(start: string | null, end: string | null, filter: AnalyticsFilter) {
  if (!start && !end) return false;
  const startsBeforeEnd = !start || new Date(start) <= filter.to;
  const endsAfterStart = !end || new Date(end) >= filter.from;
  return startsBeforeEnd && endsAfterStart;
}

function buildFeatureUsage(data: EstufaData) {
  const map = new Map<string, number>();
  for (const event of data.events.filter((item) => item.eventFamily === "feature_used")) {
    const feature =
      typeof event.metadata?.feature === "string" ? event.metadata.feature : event.eventName;
    map.set(feature, (map.get(feature) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([feature, uses]) => ({ feature, uses }))
    .sort((a, b) => b.uses - a.uses);
}

function buildCohorts(data: EstufaData, filter: AnalyticsFilter) {
  const days = [0, 7, 14, 30, 60, 90];
  const groups = new Map<string, typeof data.installations>();
  for (const installation of data.installations.filter((row) =>
    inWindow(row.installedAt, filter),
  )) {
    const cohort = format(startOfMonth(new Date(installation.installedAt)), "yyyy-MM");
    groups.set(cohort, [...(groups.get(cohort) ?? []), installation]);
  }
  const rows: Record<string, unknown>[] = [];
  for (const [cohort, installations] of [...groups.entries()].sort()) {
    for (const day of days) {
      const eligible = installations.filter(
        (row) => new Date(row.installedAt).getTime() + day * 86_400_000 <= filter.to.getTime(),
      );
      const retained =
        day === 0
          ? eligible.length
          : eligible.filter((row) => {
              const threshold = new Date(row.installedAt).getTime() + day * 86_400_000;
              return data.events.some(
                (event) =>
                  event.deploymentId === row.deploymentId &&
                  event.tenantId === row.tenantId &&
                  new Date(event.occurredAt).getTime() >= threshold &&
                  new Date(event.occurredAt) <= filter.to,
              );
            }).length;
      rows.push({
        cohort_month: cohort,
        retention_day: day,
        eligible: eligible.length,
        retained,
        retention_rate: eligible.length ? retained / eligible.length : null,
      });
    }
  }
  return rows;
}

function selectedDimensions(data: EstufaData, filter: AnalyticsFilter) {
  const deployments = data.deployments.filter((deployment) => {
    if (filter.seedId && deployment.seedId !== filter.seedId) return false;
    if (filter.deploymentId && deployment.id !== filter.deploymentId) return false;
    if (filter.platformId && deployment.platformId !== filter.platformId) return false;
    return true;
  });
  const seedIds = new Set(deployments.map((item) => item.seedId));
  const platformIds = new Set(deployments.map((item) => item.platformId));
  if (filter.seedId) seedIds.add(filter.seedId);
  if (filter.platformId) platformIds.add(filter.platformId);
  return {
    deployments,
    seeds: data.seeds.filter((seed) => seedIds.has(seed.id)),
    platforms: data.platforms.filter((platform) => platformIds.has(platform.id)),
  };
}

export function buildExportDatasets(data: EstufaData, filter: AnalyticsFilter): ExportDatasetMap {
  const metrics = calculatePortfolioMetrics({
    installations: data.installations,
    subscriptions: data.subscriptions,
    revenue: data.revenue,
    events: data.events,
    from: filter.from,
    to: filter.to,
  });
  const series = buildSeries({
    installations: data.installations,
    subscriptions: data.subscriptions,
    revenue: data.revenue,
    from: filter.from,
    to: filter.to,
    granularity: filter.granularity,
  });
  const dimensions = selectedDimensions(data, filter);
  const installations = data.installations.filter((row) =>
    overlapsWindow(row.installedAt, row.uninstalledAt, filter),
  );
  const subscriptions = data.subscriptions.filter((row) =>
    overlapsWindow(row.startedAt, row.canceledAt, filter),
  );
  const rawEvents = data.events.filter((row) => inWindow(row.occurredAt, filter));
  const metricSnapshots = data.metricSnapshots.filter((row) =>
    overlapsWindow(row.periodStart, row.periodEnd, filter),
  );
  const revenue = data.revenue.filter((row) => inWindow(row.recognizedAt, filter));
  const filteredForDerived: EstufaData = {
    ...data,
    installations,
    subscriptions,
    events: rawEvents,
    metricSnapshots,
    revenue,
  };
  return {
    summary_metrics: [
      {
        ...metrics,
        from: filter.from.toISOString(),
        to: filter.to.toISOString(),
        granularity: filter.granularity,
      },
    ],
    timeseries: series,
    funnel: buildFunnel(
      {
        installations: data.installations,
        subscriptions: data.subscriptions,
        events: data.events,
        metricSnapshots: data.metricSnapshots,
      },
      filter,
    ).map((row) => ({
      stage: row.stage,
      value: row.value,
      step_conversion_rate: row.stepConversionRate,
    })),
    platforms: dimensions.platforms,
    seeds: dimensions.seeds,
    deployments: dimensions.deployments,
    installations,
    raw_events: rawEvents,
    metric_snapshots: metricSnapshots,
    subscriptions,
    revenue,
    churn: data.subscriptions.filter((row) => inWindow(row.canceledAt, filter)),
    cohorts: buildCohorts(filteredForDerived, filter),
    feature_usage: buildFeatureUsage(filteredForDerived),
    incidents: data.incidents.filter((row) => overlapsWindow(row.openedAt, row.resolvedAt, filter)),
    support_tickets: data.supportTickets.filter((row) =>
      overlapsWindow(row.openedAt, row.resolvedAt, filter),
    ),
    reviews: data.reviews.filter((row) => inWindow(row.reviewedAt, filter)),
    releases: data.releases.filter((row) => inWindow(row.releasedAt, filter)),
    experiments: data.experiments.filter((row) => overlapsWindow(row.startsAt, row.endsAt, filter)),
    decisions: data.decisions.filter((row) => inWindow(row.decidedAt, filter)),
    lessons: data.lessons.filter((row) => inWindow(row.createdAt, filter)),
    opportunity_scores: data.opportunityScores.filter((row) => inWindow(row.scoredAt, filter)),
    research_dataset: data.researchApps.filter((row) => inWindow(row.observedAt, filter)),
  };
}

export function exportDataset(name: string, rows: unknown[], format: "csv" | "json") {
  const stamp = civilDateValue(new Date(), appConfig.timezone);
  if (format === "json")
    downloadBlob(JSON.stringify(rows, null, 2), "application/json", `${name}-${stamp}.json`);
  else downloadBlob(toCsv(rows), "text/csv;charset=utf-8", `${name}-${stamp}.csv`);
}

export async function exportAll(data: EstufaData, filter: AnalyticsFilter) {
  const datasets = buildExportDatasets(data, filter);
  const zip = new JSZip();
  zip.file(
    "manifest.json",
    JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        timeZone: appConfig.timezone,
        from: filter.from.toISOString(),
        to: filter.to.toISOString(),
        granularity: filter.granularity,
        seedId: filter.seedId ?? null,
        deploymentId: filter.deploymentId ?? null,
        platformId: filter.platformId ?? null,
        datasets: Object.keys(datasets),
      },
      null,
      2,
    ),
  );
  for (const [name, rows] of Object.entries(datasets)) {
    zip.file(`${name}.json`, JSON.stringify(rows, null, 2));
    zip.file(`${name}.csv`, toCsv(rows));
  }
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(
    blob,
    "application/zip",
    `estufa-export-${civilDateValue(new Date(), appConfig.timezone)}.zip`,
  );
}
